import type {
  BaselineSnapshot,
  CookieIssue,
  HeaderIssue,
  ScanApiReport,
  Severity,
  Status,
} from './types.js';

export async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* ignore */
  }
}

export function downloadJSON(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(href), 0);
}

export function enrichHeader(issue: HeaderIssue): HeaderIssue {
  if (issue.code === 'no_hsts' || /Strict-Transport-Security/i.test(issue.title)) {
    return {
      ...issue,
      code: 'no_hsts',
      title: issue.title || 'Missing Strict-Transport-Security',
      severity: 'high',
      fixSnippet: {
        label: 'Caddy: add HSTS',
        content:
          'header Strict-Transport-Security "max-age=31536000; includeSubDomains; preload"',
      },
      advice: 'Enable HSTS on HTTPS responses to enforce TLS and qualify for preload.',
    };
  }
  if (issue.code === 'no_csp' || /Content-Security-Policy/i.test(issue.title)) {
    return {
      ...issue,
      code: 'no_csp',
      title: issue.title || 'Missing Content-Security-Policy',
      severity: 'high',
      fixSnippet: {
        label: 'Express: basic CSP',
        content:
          "res.set('Content-Security-Policy', \"default-src 'self'; frame-ancestors 'self'; upgrade-insecure-requests\")",
      },
      advice: 'Define a CSP to control resource loading and clickjacking via frame-ancestors.',
    };
  }
  if (
    issue.code === 'no_clickjacking' ||
    /clickjacking/i.test(issue.title) ||
    /X-Frame-Options/i.test(issue.title)
  ) {
    return {
      ...issue,
      code: 'no_clickjacking',
      title: issue.title || 'No clickjacking protection',
      severity: 'medium',
      fixSnippet: {
        label: 'Caddy: frame protection',
        content: 'header X-Frame-Options "DENY"',
      },
      advice: 'Use X-Frame-Options DENY or CSP frame-ancestors to block UI redress attacks.',
    };
  }
  return issue;
}

export function mapCookieIssue(line: string): CookieIssue {
  const lc = line.toLowerCase();
  const nameMatch = /cookie\s+([A-Za-z0-9_.-]+)/i.exec(line) || /^(\w+)[=:]/.exec(line);
  const name = nameMatch ? nameMatch[1] : '—';
  const severity: Severity = /secure/.test(lc)
    ? 'high'
    : /httponly|samesite/.test(lc)
      ? 'medium'
      : 'low';
  return { name, problem: line, setBy: 'server', severity };
}

export function normalizeUrl(input: string): string {
  const t = (input || '').trim();
  if (!t) return t;
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

export function isValidUrl(u: string): boolean {
  try {
    const parsed = new URL(u);
    return !!parsed.protocol && !!parsed.hostname;
  } catch {
    return false;
  }
}

export function pslTopDomain(hostname: string): string {
  const h = (hostname || '').toLowerCase();
  const parts = h.split('.');
  if (parts.length <= 2) return h;
  const multi = new Set([
    'co.uk',
    'org.uk',
    'gov.uk',
    'ac.uk',
    'co.jp',
    'ne.jp',
    'or.jp',
    'co.kr',
    'com.au',
    'net.au',
    'org.au',
    'co.in',
    'com.br',
    'com.mx',
  ]);
  const tld2 = parts.slice(-2).join('.');
  if (multi.has(tld2)) return parts.slice(-3).join('.');
  return tld2;
}

export function registryApiBase(registryUrl: string): string {
  const base = registryUrl.replace(/\/$/, '');
  return /\/api$/.test(base) ? base : `${base}/api`;
}

export function toPermalink(u: string, gpc: boolean) {
  const q = new URLSearchParams(window.location.search);
  q.set('url', u);
  q.set('gpc', gpc ? '1' : '0');
  return `${window.location.pathname}?${q.toString()}`;
}

function baselineKey(u: string, g: boolean) {
  try {
    const base = new URL(u);
    const top = pslTopDomain(base.hostname);
    return `cmp.scanner.baseline.${top}.${g ? '1' : '0'}`;
  } catch {
    return `cmp.scanner.baseline.unknown.${g ? '1' : '0'}`;
  }
}

export function writeLocalBaseline(u: string, gpc: boolean, baseline: BaselineSnapshot) {
  try {
    const key = baselineKey(u, gpc);
    localStorage.setItem(key, JSON.stringify(baseline));
  } catch {
    /* ignore */
  }
}

export function buildScanResult(
  raw: ScanApiReport,
  normalizedUrl: string,
  gpc: boolean,
): {
  scan: {
    url: string;
    finalUrl: string;
    status: Status;
    durationMs: number;
    gpc: boolean;
    headers: { present: string[]; issues: HeaderIssue[] };
    cookies: { issues: CookieIssue[] };
    thirdParty: { newHosts: string[] };
    artifacts?: { screenshotUrl?: string; traceUrl?: string };
    raw: ScanApiReport;
  };
  presentHeaders: string[];
  headerIssues: HeaderIssue[];
  cookieIssues: CookieIssue[];
  newThirdPartyHosts: string[];
} {
  const headersPart = raw.analysis?.headers as { present?: unknown; issues?: unknown } | undefined;
  const present = Array.isArray(headersPart?.present)
    ? ((headersPart?.present as unknown[]) || []).map((s) => String(s))
    : [];
  const headerIssueLines = Array.isArray(headersPart?.issues)
    ? ((headersPart?.issues as unknown[]) || []).map((s) => String(s))
    : [];
  const headerIssues = headerIssueLines.map((line) => {
    let issue: HeaderIssue;
    if (/Strict-Transport-Security/i.test(line)) {
      issue = { code: 'no_hsts', title: 'Missing Strict-Transport-Security', severity: 'high' };
    } else if (/Content-Security-Policy/i.test(line)) {
      issue = { code: 'no_csp', title: 'Missing Content-Security-Policy', severity: 'high' };
    } else if (/clickjacking/i.test(line) || /X-Frame-Options/i.test(line)) {
      issue = {
        code: 'no_clickjacking',
        title: 'No clickjacking protection',
        severity: 'medium',
      };
    } else {
      issue = { code: 'other', title: line, severity: 'low' } as HeaderIssue;
    }
    return enrichHeader(issue);
  });
  const cookiesPart = raw.analysis?.cookies as { issues?: unknown } | undefined;
  const cookieIssueCodes = Array.isArray(cookiesPart?.issues)
    ? ((cookiesPart?.issues as unknown[]) || []).map((s) => String(s))
    : [];
  const cookieIssues = cookieIssueCodes.map(mapCookieIssue);
  const new3p = Array.isArray(raw.diff?.newThirdPartyHosts)
    ? ((raw.diff?.newThirdPartyHosts as unknown[]) || []).map((s) => String(s))
    : [];

  const scan = {
    url: raw.url || normalizedUrl,
    finalUrl: raw.finalUrl || raw.url || normalizedUrl,
    status: (raw.status === 'ok' ? 'ok' : 'fail') as Status,
    durationMs: Number(raw.durationMs || 0),
    gpc,
    headers: { present, issues: headerIssues },
    cookies: { issues: cookieIssues },
    thirdParty: { newHosts: new3p },
    artifacts: raw.artifacts || {},
    raw,
  };

  return {
    scan,
    presentHeaders: present,
    headerIssues,
    cookieIssues,
    newThirdPartyHosts: new3p,
  };
}
